import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { Reveal, CTASection, usePageMeta, Eyebrow } from "../components/ui-bits";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Block = ({ b }) => {
  switch (b.type) {
    case "heading":
      return <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-4">{b.text}</h2>;
    case "paragraph":
      return <p className="text-base leading-relaxed text-slate-600 whitespace-pre-line">{b.text}</p>;
    case "list":
      return (
        <ul className="space-y-3">
          {(b.items || []).filter((i) => i.trim()).map((item, k) => (
            <li key={k} className="flex items-start gap-3 text-[15px] text-slate-700">
              <span className="mt-2 w-2 h-2 bg-[#C5221F] shrink-0" />{item}
            </li>
          ))}
        </ul>
      );
    case "image":
      return b.src ? (
        <figure>
          <img src={b.src} alt={b.alt || ""} loading="lazy" className="w-full object-cover img-industrial border border-slate-200" />
          {b.caption && <figcaption className="mt-2.5 text-xs text-slate-500 font-mono-tech uppercase tracking-widest">{b.caption}</figcaption>}
        </figure>
      ) : null;
    case "button": {
      if (!b.label) return null;
      const cls = "group inline-flex items-center gap-3 bg-[#C5221F] hover:bg-[#A81B18] text-white font-display font-semibold px-7 py-3.5 text-xs uppercase tracking-widest transition-colors duration-300";
      const inner = <>{b.label}<ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" /></>;
      return b.href?.startsWith("/") ? <Link to={b.href} className={cls}>{inner}</Link> : <a href={b.href || "#"} className={cls}>{inner}</a>;
    }
    default:
      return null;
  }
};

const PageView = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setPage(null);
    setMissing(false);
    axios.get(`${API}/pages/${slug}`).then((r) => setPage(r.data)).catch(() => setMissing(true));
  }, [slug]);

  usePageMeta(
    page ? (page.seo_title || `${page.title} | G. R. Engineering Private Limited`) : "G. R. Engineering Private Limited",
    page ? (page.seo_description || page.subtitle || "") : ""
  );

  if (missing) {
    return (
      <section className="bg-slate-50">
        <div className="container-gre py-32 text-center">
          <p className="font-display text-3xl font-bold text-slate-900">Page not found</p>
          <p className="mt-3 text-slate-600">This page may have been unpublished or moved.</p>
          <Link to="/" className="mt-7 inline-flex items-center gap-3 bg-[#C5221F] hover:bg-[#A81B18] text-white font-display font-semibold px-7 py-3.5 text-xs uppercase tracking-widest transition-colors duration-300" data-testid="pageview-home-link">
            Back to Home <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    );
  }

  if (!page) return <div className="container-gre py-32 text-center text-slate-400">Loading…</div>;

  return (
    <>
      <section className="relative bg-[#0A1128] overflow-hidden" data-testid="custom-page-hero">
        <div className="absolute inset-0 blueprint-grid" />
        <div className="container-gre relative py-20 lg:py-28">
          <Reveal><Eyebrow light>G. R. Engineering</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h1 className="font-display mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight max-w-3xl">{page.title}</h1>
          </Reveal>
          {page.subtitle && <Reveal delay={0.16}><p className="mt-5 text-base lg:text-lg text-slate-300 max-w-2xl leading-relaxed">{page.subtitle}</p></Reveal>}
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#C5221F]" />
      </section>
      <section className="bg-white">
        <div className="container-gre py-16 lg:py-24 max-w-3xl space-y-8" data-testid="custom-page-content">
          {page.blocks?.map((b, i) => <Block key={i} b={b} />)}
        </div>
      </section>
      <CTASection />
    </>
  );
};

export default PageView;
